import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Button } from './ui/Button'
import { ErrorBoundary } from './ErrorBoundary'
import { VerifyEmailBanner } from './VerifyEmailBanner'

// Layout is the outer chrome rendered around every authenticated route.
// Top bar with brand, nav links, and the signed-in user + sign-out
// button. Children render inside an <Outlet/>.
export function Layout() {
  const auth = useAuth()
  const navigate = useNavigate()

  const navItem = (to: string, label: string) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center min-h-[44px] rounded-md px-2 py-1.5 text-sm font-medium sm:px-3 ${
          isActive
            ? 'bg-surface-2 text-cream'
            : 'text-cream/70 hover:bg-surface-2 hover:text-cream'
        }`
      }
    >
      {label}
    </NavLink>
  )

  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 sm:gap-6">
            <Link to="/" className="flex items-center" aria-label="Backlit home">
              <img src="/backlit-lockup.png" alt="Backlit" className="h-6 w-auto" />
            </Link>
            <nav className="flex gap-1">
              {navItem('/glows', 'Glows')}
              {navItem('/usage', 'Usage')}
              {navItem('/billing', 'Billing')}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-cream/70 sm:inline">
              {auth.user?.email}
            </span>
            <Button
              variant="ghost"
              onClick={async () => {
                await auth.signOut()
                navigate('/login')
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <VerifyEmailBanner />
      <main className="mx-auto max-w-6xl overflow-x-hidden px-4 py-8">
        {/* Inner boundary so a single route crash shows an inline error
            inside the chrome (header + nav stay usable) instead of
            white-screening the whole app. Keyed nothing — the route
            element below is what changes on navigation, and "Try again"
            re-renders the current route. */}
        <ErrorBoundary fallback={(error, reset) => <RouteError error={error} reset={reset} />}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  )
}

// RouteError is the inline fallback rendered inside the Layout chrome when
// a single route's subtree throws. The outer boundary in main.tsx is the
// last-resort full-screen fallback; this one keeps the app usable.
function RouteError({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-red-500/25 bg-red-500/10 p-6 text-sm text-red-300"
    >
      <h2 className="text-base font-semibold">This page hit an error</h2>
      <p className="mt-1 text-red-300">
        {error.message || 'An unexpected error occurred while rendering this page.'}
      </p>
      <Button variant="secondary" className="mt-4" onClick={reset}>
        Try again
      </Button>
    </div>
  )
}
